// Market-index data model + pure helpers for the Plate Detail v2 "Market
// Information" section.
//
// Architecture principle (see project memory / task brief): this is NOT a
// per-registration bespoke graph. We ship a small set of PRECOMPUTED PlateX
// index series (one line per plate style/segment) as a static asset, route the
// current plate to the right series by its format, render that series in the
// prototype graph style, then overlay the plate's OWN verified sale markers.
// The index line is reusable across every plate in the same segment; only the
// markers differ per registration.
//
// Series + routing follow the PlateX Plate Price Index v2 (platex-index
// SITE_INDEX_SPEC.md; index 2026.10-v2): every plate uses its OWN FORMAT's
// series at the DVLA-auction level, word and name plates included. This is the
// same rule as platex-valuation-lab/src/valuation/index_client/routing.py.

/** One yearly point of a modelled index series. */
export interface IndexPoint {
  year: number;
  value: number;
  ci_lo: number | null;
  ci_hi: number | null;
  density: string; // 'dense' | 'thin' | 'suppressed' | ''
}

/** A single named index series as shipped in the JSON asset. */
export interface IndexSeries {
  points: IndexPoint[];
  year_min: number;
  year_max: number;
  n_points: number;
  // v2 additions (optional so older assets still type-check)
  source_series?: string; // the v2 series this key carries, e.g. short_plate_vw -> dateless
  label?: string;
  status?: string; // 'published' | 'experimental' (internal only, not shown to visitors)
}

/** Root shape of assets/market-index/platex-index-series.json. */
export interface IndexSeriesFile {
  meta: {
    source: string;
    base_year: number;
    rebased_display: string;
    generated_at: string;
    note: string;
  };
  series: { [name: string]: IndexSeries };
}

/** The plate-format keys the v2 page already derives (never guessed). */
export type MarketFormatKey =
  | 'dateless'
  | 'prefix'
  | 'suffix'
  | 'current'
  | 'ni'
  | null;

// Editorial label + one-line description per series, so the card copy stays
// data-true to whichever series was actually routed (no hardcoded per-plate
// prose). Keyed by the resolved series name.
const V2_BLURB =
  'A mix-adjusted index of plates sold at auction (DVLA auctions and auction houses), like for like. The shaded band is the 95% range.';

// Card title + one-line plain subtitle (the methodology blurb sits behind the
// card's "How this is calculated" expander). Titles follow the plate's format.

export const SERIES_LABELS: {
  [series: string]: { title: string; sub: string; tag: string; blurb: string; short: string };
} = {
  dateless: {
    title: 'Dateless market index',
    sub: 'How auction prices for dateless plates have moved, like for like.',
    tag: 'Modelled index',
    blurb: V2_BLURB,
    short: 'Dateless plates',
  },
  NI: {
    title: 'Northern Ireland market index',
    sub: 'How auction prices for Northern Ireland plates have moved, like for like.',
    tag: 'Modelled index',
    blurb: V2_BLURB,
    short: 'Northern Ireland plates',
  },
  prefix: {
    title: 'Prefix market index',
    sub: 'How auction prices for prefix plates have moved, like for like.',
    tag: 'Modelled index',
    blurb: V2_BLURB,
    short: 'Prefix plates',
  },
  suffix: {
    title: 'Suffix market index',
    sub: 'How auction prices for suffix plates have moved, like for like.',
    tag: 'Modelled index',
    blurb: V2_BLURB,
    short: 'Suffix plates',
  },
  current: {
    title: 'Current market index',
    sub: 'How auction prices for current-style plates have moved, like for like.',
    tag: 'Modelled index',
    blurb: V2_BLURB,
    short: 'Current-style plates',
  },
  composite: {
    title: 'PlateX market index',
    sub: 'How auction prices for all plates have moved, like for like.',
    tag: 'Modelled index',
    blurb: V2_BLURB,
    short: 'All plates',
  },
};

/**
 * Route a plate to its index series: its own format (tested rule, index v2).
 * Every dateless plate uses 'dateless' (single digits included); NI uses 'NI';
 * prefix and suffix each have their own series; current uses 'current'.
 * Returns null only when the format is unknown (caller shows the fallback).
 */
export function routeToSeries(
  formatKey: MarketFormatKey,
  _compactReg: string
): string | null {
  switch (formatKey) {
    case 'prefix':
      return 'prefix';
    case 'suffix':
      return 'suffix';
    case 'current':
      return 'current';
    case 'ni':
      return 'NI';
    case 'dateless':
      return 'dateless';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Cohort verified-sales scatter (prototype "Chart B").
//
// Where the index series (above) is a smooth MODELLED line, the cohort dataset
// is the RAW verified auction clearances behind a plate's reference class —
// one dot per real sale. Same architecture principle: a small set of
// PRECOMPUTED per-cohort point clouds shipped as a static asset, routed by the
// plate's structural class, with the plate's OWN sale highlighted on top.
// Source: platex-index phase1b (all-in buyer-cost space).
// ---------------------------------------------------------------------------

/** One verified auction clearance in a cohort scatter. */
export interface CohortSalePoint {
  plate: string; // canonical, e.g. "30O"
  cls: string; // structural class, e.g. "NN_L" | "L_NN"
  y: number; // fractional sale year (year + month/12)
  ym: string; // "YYYY-MM" for tooltips
  price: number; // all-in GBP (already pounds, not pence)
}

/** Combined cohort annual median (both sub-formats pooled). */
export interface CohortMedian {
  year: number;
  value: number;
  n: number;
}

/** Combined cohort annual quartiles (q1/median/q3), both sub-formats pooled. */
export interface CohortQuartile {
  year: number;
  q1: number;
  median: number;
  q3: number;
  n: number;
}

/**
 * Pooled cohort statistics over the window — the basis for the upcoming
 * "Observable cohort value" band (NOT a per-plate valuation). Computed offline
 * from the cleaned cohort points (numeric-look O registrations excluded).
 */
export interface CohortSummary {
  window: number[];
  n: number;
  q1: number;
  median: number;
  q3: number;
  recent_window: number[];
  recent_n: number;
  recent_q1: number;
  recent_median: number;
  recent_q3: number;
}

/** A single named cohort as shipped in the JSON asset. */
export interface CohortDef {
  label: string;
  primary_class: string;
  sister_class: string;
  primary_label: string;
  sister_label: string;
  year_min: number;
  year_max: number;
  n: number;
  points: CohortSalePoint[];
  medians: CohortMedian[];
  // Present on the cleaned cohort asset; consumed by the upcoming Observable
  // Cohort Value feature. Optional so older assets still type-check.
  quartiles?: CohortQuartile[];
  summary?: CohortSummary;
  // Set on cohorts the page builds itself from a dateless layout file: the
  // plain-language comparable rule, and that file's data note.
  basis?: string;
  note?: string;
}

/** Root shape of assets/market-index/platex-cohort-sales.json. */
export interface CohortSalesFile {
  meta: {
    source: string;
    generated_at: string;
    price_floor_gbp: number;
    window: number[];
    note: string;
  };
  cohorts: { [key: string]: CohortDef };
}

/**
 * Route a plate to its verified-sales cohort key (or null for no cohort).
 *
 * First cut covers the "3-character single-letter dateless" cohort only —
 * exactly the structures the prototype's Chart B shows: NN_L (two digits then
 * one letter, e.g. 72 K) and its sister L_NN (one letter then two digits,
 * e.g. K 72). Every other plate returns null and the scatter is omitted
 * (the modelled index chart still renders for it). This mirrors the index
 * routing's "nearest available, else quiet fallback" discipline.
 */
export function routeToCohort(
  formatKey: MarketFormatKey,
  compactReg: string
): string | null {
  if (formatKey !== 'dateless') return null;
  const p = (compactReg || '').toUpperCase();
  if (/^\d{2}[A-Z]$/.test(p) || /^[A-Z]\d{2}$/.test(p)) {
    return 'dateless_3char_1letter';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Every other dateless layout (1 A, AB 1, A 123, AB 12, ABC 123, 1234 AB …).
//
// One raw sales file per layout (letters x digits, both arrangements pooled):
// assets/market-index/cohorts/dateless-L{letters}D{digits}.json, built by
// analysis/market-cohorts/build_dateless_layouts.py. The page picks the
// comparable subset per plate (selectDatelessComparables) and derives the
// yearly medians itself, then renders it through the same CohortDef path.
// ---------------------------------------------------------------------------

/** A dateless plate split into its letters, digits and arrangement. */
export interface DatelessParts {
  letters: string;
  digits: string;
  digitsFirst: boolean;
}

export function parseDatelessParts(compact: string): DatelessParts | null {
  const p = (compact || '').toUpperCase();
  let m = /^(\d{1,4})([A-Z]{1,3})$/.exec(p);
  if (m) return { digits: m[1], letters: m[2], digitsFirst: true };
  m = /^([A-Z]{1,3})(\d{1,4})$/.exec(p);
  if (m) return { letters: m[1], digits: m[2], digitsFirst: false };
  return null;
}

/** Root shape of assets/market-index/cohorts/dateless-L{n}D{n}.json. */
export interface DatelessLayoutFile {
  meta: {
    source: string;
    generated_at: string;
    data_to: string;
    price_floor_gbp: number;
    window: number[];
    note: string;
  };
  letters: number;
  digits: number;
  primary_class: string; // 'digits_first'
  sister_class: string; // 'letters_first'
  primary_label: string;
  sister_label: string;
  year_min: number;
  year_max: number;
  n: number;
  rows: [string, number, number][]; // [plate, fractional sale year, £ all-in]
}

/** Root shape of the companion dateless-L{n}D{n}-early.json (pre-2016 sales). */
export interface DatelessEarlyFile {
  year_min: number;
  n: number;
  rows: [string, number, number][];
}

/**
 * The layout file key for a dateless plate, or null when the plate is not
 * dateless or is served by the live 3-character single-letter cohort.
 */
export function routeToDatelessLayout(
  formatKey: MarketFormatKey,
  compactReg: string
): string | null {
  if (formatKey !== 'dateless') return null;
  if (routeToCohort(formatKey, compactReg)) return null;
  const d = parseDatelessParts(compactReg);
  if (!d) return null;
  return `dateless-L${d.letters.length}D${d.digits.length}`;
}

/** Layouts small enough that every sale in them is a fair comparable. */
export const WHOLE_LAYOUT_MAX = 400;

/**
 * How a comparable is chosen against a subject plate. Shared by the verified
 * sales scatter and the live asking-price strip so both cards show the same
 * kind of plate:
 *   'letters'  same letters, any number, either arrangement
 *   'number'   same number, either arrangement
 *   'numberInitial'  same number AND same first letter (used when the bare
 *              number is too common to mean anything, e.g. ABC 1)
 *   'initial'  same first letter (widening step for thin cohorts)
 */
export type DatelessMatch = 'letters' | 'number' | 'numberInitial' | 'initial';

export function datelessMatch(
  subject: DatelessParts,
  other: DatelessParts,
  crowdedNumber: boolean,
  widen: boolean
): DatelessMatch | null {
  if (other.letters === subject.letters) return 'letters';
  if (other.digits === subject.digits) {
    if (!crowdedNumber) return 'number';
    if (other.letters[0] === subject.letters[0]) return 'numberInitial';
  }
  if (widen && other.letters[0] === subject.letters[0]) return 'initial';
  return null;
}

/**
 * Number 1 plates carry their own premium and are valued apart from 2 to 9
 * (owner rule: HS 1 compares with ??1 / 1?? plates, 2 TGE never with 1 RRL).
 * In single-number layouts the two never mix; other layouts are unaffected.
 * Exception: 1-letter, 1-number plates are so few that they compare with each
 * other whatever the number, and the card says number 1 sells at a premium.
 */
export function sameNumberClass(subject: DatelessParts, other: DatelessParts): boolean {
  if (subject.digits.length !== 1 || subject.letters.length === 1) return true;
  return (subject.digits === '1') === (other.digits === '1');
}

/** Above this many same-number sales the number alone is not a comparable. */
export const CROWDED_NUMBER = 150;
/** Layouts with fewer sales than this since 2016 always show every year. */
export const TINY_LAYOUT = 50;
/** Below this many comparable sales the cohort widens to the first letter. */
export const THIN_COHORT = 12;

/** Yearly median of the given sales (years with fewer than 3 sales skipped). */
export function yearlyMedians(
  pts: { y: number; price: number }[],
  yearMin: number,
  yearMax: number
): CohortMedian[] {
  const by = new Map<number, number[]>();
  for (const p of pts) {
    const yr = Math.floor(p.y);
    if (yr < yearMin || yr > yearMax) continue;
    (by.get(yr) || by.set(yr, []).get(yr)!).push(p.price);
  }
  const out: CohortMedian[] = [];
  for (const [year, v] of Array.from(by.entries()).sort((a, b) => a[0] - b[0])) {
    if (v.length < 3) continue;
    v.sort((a, b) => a - b);
    const mid = v.length >> 1;
    const value = v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
    out.push({ year, value: Math.round(value), n: v.length });
  }
  return out;
}

/** Plain-language description of a comparable rule, for card copy. */
export function describeDatelessCohort(
  subject: DatelessParts,
  whole: boolean,
  crowdedNumber: boolean,
  widened: boolean,
  layoutName: string
): string {
  const numberClass =
    subject.digits.length !== 1
      ? ''
      : subject.letters.length === 1
      ? '. Number 1 plates usually sell at a premium to 2 to 9'
      : subject.digits === '1'
      ? ', number 1 plates only'
      : ', numbers 2 to 9 only';
  if (whole) return `every ${layoutName} plate${numberClass}`;
  const parts = [
    `${subject.letters.length === 1 ? 'the letter' : 'the letters'} ${subject.letters}`,
  ];
  parts.push(
    crowdedNumber
      ? `the number ${subject.digits} with the same first letter`
      : `the number ${subject.digits}`
  );
  if (widened) parts.push(`the first letter ${subject.letters[0]}`);
  const last = parts.pop();
  return `${layoutName} plates sharing ${parts.join(', ')} or ${last}${numberClass}`;
}

/** e.g. 3 letters + 3 digits -> "3-letter, 3-number". */
export function datelessLayoutName(letters: number, digits: number): string {
  return `${letters}-letter, ${digits}-number`;
}

/**
 * Linear-interpolated index value for an arbitrary year (used to pin a sale
 * marker onto the index line at its sale year). Mirrors loader.py:
 * before-start / after-end clamp to the nearest endpoint; otherwise
 * interpolate. Returns null for an empty series.
 */
export function indexValueAt(points: IndexPoint[], year: number): number | null {
  if (!points || points.length === 0) return null;
  if (year <= points[0].year) return points[0].value;
  if (year >= points[points.length - 1].year) {
    return points[points.length - 1].value;
  }
  for (let i = 0; i < points.length - 1; i++) {
    const lo = points[i];
    const hi = points[i + 1];
    if (year >= lo.year && year <= hi.year) {
      const t = (year - lo.year) / (hi.year - lo.year);
      return lo.value + t * (hi.value - lo.value);
    }
  }
  return points[points.length - 1].value;
}
